import { LOGO_PATHS, LOGO_VIEW_BOX } from '@/components/logo-art'
import { cn } from '@/lib/utils'

/**
 * The saltseed mark: two strands of connected nodes forming an S around a seed.
 *
 * Inlined rather than `<img src="/logo.svg">` for one reason: the console has an EXPLICIT theme
 * toggle (a `.dark` class on <html>), and an <img> cannot see it — it would only follow the OS
 * setting baked into the file, so a reader who flipped the console to dark would keep a mark whose
 * ink strand is invisible against the background. Inline, the same rule is a `dark:` variant.
 *
 * The artwork itself lives in `logo-art.ts`; see the note there about its two other copies.
 */

/** The ink strand. #001d46 on light; on dark it would vanish, so it lifts. */
const INK = 'fill-[#001d46] dark:fill-[#8fb0e0]'
/** The link strand — the teal the rest of the palette is built around. */
const LINK = 'fill-[#008f9b] dark:fill-[#23b3bf]'

/**
 * The mark alone.
 *
 * Taller than it is wide (525 x 614), so the default sizes the HEIGHT and lets the width follow.
 * `size-6` on a non-square viewBox letterboxes: the mark renders smaller than everything sized
 * beside it and looks accidentally shrunken.
 */
export function LogoMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox={LOGO_VIEW_BOX}
      fill="none"
      role="img"
      aria-hidden="true"
      className={cn('h-6 w-auto', className)}
    >
      <path className={LINK} d={LOGO_PATHS.link} />
      <path className={INK} d={LOGO_PATHS.ink} />
      <ellipse
        className={cn('fill-[#c39b64]', 'stroke-[#001d46] dark:stroke-[#8fb0e0]')}
        strokeWidth="1"
        cx={LOGO_PATHS.seed.cx}
        cy={LOGO_PATHS.seed.cy}
        rx={LOGO_PATHS.seed.rx}
        ry={LOGO_PATHS.seed.ry}
      />
    </svg>
  )
}

export function Wordmark({ className }: { className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      <LogoMark className="h-[1.35em] w-auto" />
      <span className="text-[1.02em] font-semibold tracking-[-0.02em]">saltseed</span>
    </span>
  )
}
